import { Validators, FormGroup, FormArray, FormControl } from '@angular/forms';
import { secureValidator } from '../validators/secure-validator';
import { customMandatoryValidator } from '../validators/custom-mandatory-validator';
import { FormConstants } from '../../core/globals/form-constants';
import { startWith, pairwise } from 'rxjs/operators';
import { emailValidator } from '../validators/email-validator';
import { phoneValidator } from '../validators/phone-validator';

export class BaseFormGroup {
  public formDetails: FormGroup;
  public formConstants = FormConstants;

  getFormControl = (formControlName: string, formGroup?: FormGroup): any => {
    // tslint:disable-next-line: no-string-literal
    const formGroupObject = formGroup || this['formDetails'];
    const formControlObject = formGroupObject.get(formControlName) || {};
    return formControlObject;
  }

  getValidations = (fieldConfig: any, formGroup: FormGroup): any[] => {
    const validations = [];
    if (fieldConfig.mandatory) {
      if (fieldConfig.visibility.elementKey) {
        validations.push(customMandatoryValidator.bind(this, fieldConfig, formGroup));
      } else {
        validations.push(Validators.required);
      }
    }

    if (fieldConfig.elementType === FormConstants.elementTypes.text
        || fieldConfig.elementType === FormConstants.elementTypes.dropdownAndInput
        || fieldConfig.elementType === FormConstants.elementTypes.dropdownAndSearchAndInput) {
      validations.push(secureValidator);
    }

    if (fieldConfig.elementType === FormConstants.elementTypes.incrementor && fieldConfig.values.length) {
      validations.push(Validators.max(Number(fieldConfig.values[0])));
    }

    if (fieldConfig.regex) {
      if (fieldConfig.regex === 'email_check') {
        validations.push(emailValidator);
      } else if (fieldConfig.regex === 'phone_check') {
        validations.push(phoneValidator);
      } else {
        const generalRegex = new RegExp(fieldConfig.regex);
        validations.push(Validators.pattern(generalRegex));
      }
    }
    return validations;
  }

  checkVisibility = (fieldConfig: any, formDetails?: FormGroup) => {
    if(!fieldConfig){
      return false;
    }
    if (fieldConfig.visibility.elementKey) {
      if (formDetails && formDetails instanceof FormGroup && !formDetails.get(fieldConfig.visibility?.elementKey)) {
        return false;
      }
      const conditionMet = fieldConfig.visibility.elementValue.indexOf(formDetails.get(fieldConfig.visibility.elementKey).value) > -1;
      return conditionMet ? fieldConfig.visibility.visible : !fieldConfig.visibility.visible;
    } else {
      return fieldConfig.visibility.visible;
    }
  }

  markFormGroupDirty(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup || control instanceof FormArray ) {
        this.markFormGroupDirty(control);
      } else {
        control.markAsDirty();
      }
    });
  }

  updateGroupValidity(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup || control instanceof FormArray ) {
        this.updateGroupValidity(control);
      } else {
        control.updateValueAndValidity();
      }
    });
  }

  cloneAbstractControl(control: any): any {
    let newControl: any;

    if (control instanceof FormGroup) {
      const formGroup = new FormGroup({}, control.validator, control.asyncValidator);
      const controls = control.controls;
      Object.keys(controls).forEach(key => {
        formGroup.addControl(key, this.cloneAbstractControl(controls[key]));
      });
      newControl = formGroup;
    } else if (control instanceof FormArray) {
      const formArray = new FormArray([], control.validator, control.asyncValidator);
      control.controls.forEach(formControl => formArray.push(this.cloneAbstractControl(formControl)));
      newControl = formArray;
    } else if (control instanceof FormControl) {
      newControl = new FormControl(control.value, control.validator, control.asyncValidator) as any;
    } else {
      throw new Error('Error: unexpected control value');
    }

    if (control.disabled) {
      newControl.disable({onlySelf: false, emitEvent: false});
    }

    return newControl;
  }

  constructForm(formGroup: FormGroup, fields: any[], excludeFunction?: (arg: any) => boolean) {
    for (const field of fields) {
      const excludeField = excludeFunction ? excludeFunction(field) : false;
      // tslint:disable-next-line: max-line-length
      if (field.config.elementType !== 'button' &&  !excludeField
        && ((field.config.visibility.visible || field.config.visibility.elementKey)
        || (!field.config.visibility.visible || !field.config.visibility.elementKey && field.config.mandatory))) {
        formGroup.addControl(
          field.elementKey,
          new FormControl(this.getDefaultValue(field.config), this.getValidations(field.config, formGroup))
        );
      }
    }
  }

  getDefaultValue(config: any): any {
// tslint:disable-next-line: max-line-length
    if (config.elementType === FormConstants.elementTypes.incrementor  || config.elementType === FormConstants.elementTypes.number && config.defaultValue.length) {
      return Number(config.defaultValue);
    } else if (config.elementType === FormConstants.elementTypes.toggle) {
      if (config.defaultValue && config.defaultValue.length) {
        return config.defaultValue.toLowerCase() === 'false' ? false :  true;
      } else {
        return false;
      }
    }
    return config.defaultValue;
  }

  clearFieldsOnHide(formGroup: FormGroup, triggerObj: any) {
    for (const field of triggerObj.dependants) {
      if (formGroup.get(field.elementKey) && !this.checkVisibility(field.config, formGroup)) {
        formGroup.get(field.elementKey).reset();
      }
    }
  }

  setListenersForTriggerFields(formGroup: FormGroup, fields: any[], watchers: any[]) {
    const triggerFields: any = {};
    for (const field of fields) {
      const triggerElementKey = field.config.visibility.elementKey;
      if (triggerElementKey) {
        if (triggerFields[triggerElementKey]) {
          triggerFields[triggerElementKey].dependants.push(field);
        } else {
          triggerFields[triggerElementKey] = {
            triggerKey: triggerElementKey,
            dependants: [field]
          };
        }
      }
    }
    if (watchers) {
      Object.values(triggerFields).forEach((triggerObj: any) => {
        watchers.push(
          formGroup.get(triggerObj.triggerKey).valueChanges
            .pipe(startWith(null), pairwise()).subscribe(this.clearFieldsOnHide.bind(this, formGroup, triggerObj))
        );
      });
    }
  }

  public getConfigForElementKey(config: any, elementKey: string): any {
    if (config.elementKey === elementKey) {
      return config;
    }
    if (config.pageConfigList) {
      for (const page of config.pageConfigList) {
        const hasMatch = this.getConfigForElementKey(page, elementKey);
        if (hasMatch) { return hasMatch; }
      }
    } else if (config.sectionConfigList) {
      for (const section of config.sectionConfigList) {
        const hasMatch = this.getConfigForElementKey(section, elementKey);
        if (hasMatch) { return hasMatch; }
      }
    } else if (config.elementConfigList) {
      for (const element of config.elementConfigList) {
        const hasMatch = this.getConfigForElementKey(element, elementKey);
        if (hasMatch) { return hasMatch; }
      }
    } else if (config instanceof Array) {
      for (const element of config) {
        const hasMatch = this.getConfigForElementKey(element, elementKey);
        if (hasMatch) { return hasMatch; }
      }
    }
    return false;
  }

  evaluateFieldStates(form: FormGroup | FormArray, config: any, editConfig: any) {
    if (form instanceof FormGroup) {
      for (const controlKey of Object.keys(form.controls)) {
        const control = form.controls[controlKey];
        if (control instanceof FormControl && !control.disabled) {
          const fieldConfig = this.getConfigForElementKey(config, controlKey);
          const fieldCategory = fieldConfig && fieldConfig.config.editMode && fieldConfig.config.editMode[editConfig.status];
          if (!fieldCategory || editConfig[fieldCategory] === 0) {
            // control.disable({onlySelf: true});
          }
        }
      }
    } else if (form instanceof FormArray) {
      for (const formGroup of form.controls) {
        if (formGroup instanceof FormGroup) {
          this.evaluateFieldStates(formGroup, config, editConfig);
        }
      }
    }
  }

  checkButtonVisibilityForEdit(controlConfig: any, editConfig: any) {
    const fieldCategory = controlConfig && controlConfig.config.editMode && controlConfig.config.editMode[editConfig.status];
    if (!fieldCategory || editConfig[fieldCategory] === 0) {
      return false;
    }
    return true;
  }
}
